import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import {
    DISCOUNT_APPLY_SHORTCUT,
    DISCOUNT_CANCEL_SHORTCUT,
    renderWithKeyShortcuts,
    ShortcutHint,
} from '@/components/shortcuts';

const renderCancelButton = renderWithKeyShortcuts(
    'button',
    DISCOUNT_CANCEL_SHORTCUT.combination,
);
const renderApplyButton = renderWithKeyShortcuts(
    'button',
    DISCOUNT_APPLY_SHORTCUT.combination,
);

type DiscountDialogFooterProps = {
    /** The dialog edits an existing discount, so it offers Remove. */
    canRemove: boolean;
    onRemove: () => void;
    onCancel: () => void;
    canApply: boolean;
    onApply: () => void;
};

/**
 * The dialog's actions (render-only): Remove at the left, only when editing
 * (no key of its own; Tab reaches it), then Cancel (`Esc`) and Apply
 * (`Enter`) with their keycaps. The row wraps when Georgian labels don't fit:
 * Remove stays left, Cancel and Apply move down together.
 */
export const DiscountDialogFooter: React.FC<DiscountDialogFooterProps> = ({
    canRemove,
    onRemove,
    onCancel,
    canApply,
    onApply,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Modal.Footer className="flex-wrap">
            {canRemove ? (
                <Button
                    className="mr-auto"
                    onPress={onRemove}
                    variant="danger-soft"
                    {...getNodeTestIdProp('seller-discount-remove')}
                >
                    {t('seller.discount.remove')}
                </Button>
            ) : null}
            <div className="ml-auto flex gap-2">
                <Button
                    onPress={onCancel}
                    render={renderCancelButton}
                    variant="outline"
                    {...getNodeTestIdProp('seller-discount-cancel')}
                >
                    {t('seller.discount.cancel')}
                    <ShortcutHint
                        placement="control"
                        shortcut={DISCOUNT_CANCEL_SHORTCUT}
                    />
                </Button>
                <Button
                    isDisabled={!canApply}
                    onPress={onApply}
                    render={renderApplyButton}
                    {...getNodeTestIdProp('seller-discount-apply')}
                >
                    {t('seller.discount.apply')}
                    <ShortcutHint
                        placement="control"
                        shortcut={DISCOUNT_APPLY_SHORTCUT}
                    />
                </Button>
            </div>
        </Modal.Footer>
    );
};
